import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, formatApiError, inputClass, Kicker, Label, PrimaryButton, SecondaryButton, Spinner } from "@/components/employer/portal/shared";

const EMPTY = {
    title: "", sector: "", country: "", city: "", vacancies: 1, employment_type: "",
    salary: "", currency: "", experience_required: "", education_required: "",
    skills: "", languages: "", description: "", responsibilities: "", benefits: "",
    accommodation: "", transportation: "", contract_duration: "", application_deadline: "",
    visa_info: "", required_documents: "", status: "draft",
};

const SECTIONS = [
    ["Role", [["title", "Job title", "text", true], ["sector", "Sector"], ["country", "Country"], ["city", "City / location"], ["vacancies", "Number of vacancies", "number"], ["employment_type", "Employment type"]]],
    ["Compensation & Requirements", [["salary", "Salary"], ["currency", "Currency"], ["experience_required", "Experience required"], ["education_required", "Education required"], ["skills", "Skills (comma separated)"], ["languages", "Languages (comma separated)"]]],
    ["Mobility & Timeline", [["accommodation", "Accommodation"], ["transportation", "Transportation"], ["contract_duration", "Contract duration"], ["application_deadline", "Application deadline", "date"], ["visa_info", "Visa / work-permit information"], ["required_documents", "Required documents (comma separated)"]]],
];

export default function EmployerJobForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const editId = params.get("edit");
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(Boolean(editId));
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [saved, setSaved] = useState("");

    useEffect(() => {
        if (!editId) return;
        apiFetch(`/employer/jobs/${editId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((job) => {
                if (!job) {
                    setLoading(false);
                    navigate("/employer/jobs");
                    return;
                }
                setForm({
                    ...EMPTY,
                    ...job,
                    vacancies: job.vacancies || job.positions || 1,
                    sector: job.sector || job.category || "",
                    application_deadline: job.application_deadline || job.deadline || "",
                    skills: (job.skills || []).join(", "),
                    languages: (job.languages || []).join(", "),
                    responsibilities: (job.responsibilities || []).join("\n"),
                    required_documents: (job.required_documents || []).join(", "),
                });
                setLoading(false);
            });
    }, [editId, navigate]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const payload = useMemo(() => ({ ...form, vacancies: Number(form.vacancies) || 1 }), [form]);

    const save = async (targetStatus) => {
        if (busy) return;
        setBusy(targetStatus);
        setError("");
        setSaved("");
        const body = { ...payload, status: targetStatus };
        const res = await apiFetch(editId ? `/employer/jobs/${editId}` : "/employer/jobs", {
            method: editId ? "PATCH" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
            setSaved(targetStatus === "pending_review" ? "Submitted for review" : "Draft saved");
            if (!editId && data.id) {
                navigate(`/employer/jobs/new?edit=${data.id}`, { replace: true });
            }
            if (targetStatus === "pending_review") {
                setTimeout(() => navigate("/employer/jobs"), 700);
            }
        } else {
            setError(formatApiError(data.detail, "The job could not be saved."));
        }
        setBusy("");
    };

    return (
        <EmployerLayout
            title={editId ? "Edit Job" : "Create Job"}
            subtitle="Draft at your pace. Submit for Afrinexa review when the vacancy is ready — publication is unlocked only for verified employers."
        >
            {loading ? <Spinner /> : (
                <div className="grid gap-10 xl:grid-cols-[0.72fr_0.28fr]" data-testid="employer-job-form">
                    <div className="space-y-8">
                        {SECTIONS.map(([title, fields]) => (
                            <section key={title} className="border border-ink/10 bg-ivory p-6 sm:p-7">
                                <Kicker>{title}</Kicker>
                                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                    {fields.map(([key, label, type, wide]) => (
                                        <div key={key} className={wide ? "sm:col-span-2" : ""}>
                                            <Label>{label}</Label>
                                            <input data-testid={`job-form-${key}`} type={type || "text"} min={type === "number" ? 1 : undefined} value={form[key] || ""} onChange={set(key)} className={inputClass} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}

                        <section className="border border-ink/10 bg-ivory p-6 sm:p-7">
                            <Kicker>Role Details</Kicker>
                            <div className="mt-6 space-y-5">
                                <div><Label>Job description</Label><textarea data-testid="job-form-description" rows={6} value={form.description} onChange={set("description")} className={`${inputClass} resize-y`} /></div>
                                <div><Label>Responsibilities (one per line)</Label><textarea data-testid="job-form-responsibilities" rows={6} value={form.responsibilities} onChange={set("responsibilities")} className={`${inputClass} resize-y`} /></div>
                                <div><Label>Benefits</Label><textarea data-testid="job-form-benefits" rows={4} value={form.benefits} onChange={set("benefits")} className={`${inputClass} resize-y`} /></div>
                            </div>
                        </section>

                        {error && <p data-testid="job-form-error" className="border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}
                        <div className="flex flex-wrap items-center gap-3">
                            <SecondaryButton data-testid="job-save-draft" onClick={() => save("draft")} disabled={Boolean(busy)}>
                                {busy === "draft" ? "Saving…" : "Save Draft"}
                            </SecondaryButton>
                            <PrimaryButton data-testid="job-submit-review" busy={busy === "pending_review"} onClick={() => save("pending_review")}>
                                Submit for Review
                            </PrimaryButton>
                            {saved && <span data-testid="job-saved" className="inline-flex items-center gap-1.5 text-sm text-forest"><CheckCircle2 size={15} /> {saved}</span>}
                        </div>
                    </div>

                    <aside className="h-fit border border-ink/10 bg-night p-7 text-ivory xl:sticky xl:top-8">
                        <Kicker dark>Publication rule</Kicker>
                        <p className="mt-5 font-serif text-2xl font-medium leading-snug">Only verified employers can submit vacancies for review.</p>
                        <p className="mt-4 text-sm leading-relaxed text-ivory/55">
                            Drafts remain private. Afrinexa reviews submitted roles before they become publicly visible on the jobs marketplace.
                        </p>
                        <div className="mt-7 space-y-3 border-t border-ivory/10 pt-6 text-xs uppercase tracking-[0.16em] text-ivory/45">
                            <p>Draft → Pending Review</p>
                            <p>Afrinexa Moderation → Published</p>
                            <p>Pause / Close anytime after publication</p>
                        </div>
                    </aside>
                </div>
            )}
        </EmployerLayout>
    );
}
